import { useQuery } from '@tanstack/react-query';
import {
  CalendarClock,
  CornerDownRight,
  Forward,
  Link2,
  ListChecks,
  MessageSquareQuote,
  PartyPopper,
  Send,
} from 'lucide-react';

import { telegram as telegramApi } from '@/lib/api/endpoints';
import { useT } from '@/lib/i18n';
import { Page } from '@/routes/Today';

/**
 * The Telegram manual, written from the user's side of the screen.
 *
 * The bot's own welcome message says most of this once — and is gone the
 * moment the chat scrolls. This page is the place a person can come back to
 * when they cannot remember whether it was "!" or "!!", which is precisely
 * when they will not remember where the welcome message went.
 */
export function GuideRoute() {
  const t = useT();
  // Personalize with the real bot name when the server has one configured;
  // the syntax is worth documenting even when it does not.
  const { data } = useQuery({
    queryKey: ['telegram', 'status'],
    queryFn: () => telegramApi.status(),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const bot = data?.bot_username ? `@${data.bot_username}` : 'MirOnDo bot';

  return (
    <Page>
      <h1 className="mb-2 text-xl font-semibold tracking-tight">{t('guide.title')}</h1>
      <p className="mb-8 max-w-prose text-sm text-content-subtle">{t('guide.intro')}</p>

      <GuideSection icon={<Link2 />} title={t('guide.connect.title')}>
        <p>{t('guide.connect.body', { bot })}</p>
      </GuideSection>

      <GuideSection icon={<Send />} title={t('guide.typing.title')}>
        <p>{t('guide.typing.lead')}</p>
        <div className="my-4 space-y-2.5">
          <Example message={t('guide.typing.ex1')} means={t('guide.typing.ex1.means')} />
          <Example message={t('guide.typing.ex2')} means={t('guide.typing.ex2.means')} />
          <Example message={t('guide.typing.ex3')} means={t('guide.typing.ex3.means')} />
        </div>
        <p>{t('guide.typing.understands')}</p>
        <p className="text-content-subtle">{t('guide.typing.untouched')}</p>
      </GuideSection>

      <GuideSection icon={<ListChecks />} title={t('guide.list.title')}>
        <p>{t('guide.list.lead')}</p>
        <Conversation
          message={t('guide.list.example')}
          reply={t('guide.list.reply')}
        />
        <p>{t('guide.list.alt')}</p>
        <div className="my-4">
          <Example message={t('guide.list.altExample')} />
        </div>
        <p>{t('guide.list.boxes')}</p>
        <p>{t('guide.list.combine')}</p>
        <div className="my-4">
          <Example message={t('guide.list.combineExample')} />
        </div>
      </GuideSection>

      <GuideSection icon={<CornerDownRight />} title={t('guide.subtask.title')}>
        <p>{t('guide.subtask.lead')}</p>
        <div className="my-4">
          <Example
            message={t('guide.subtask.example')}
            means={t('guide.subtask.example.means')}
          />
        </div>
        <p>{t('guide.subtask.buttons')}</p>
        <div className="my-4">
          <Example message={t('guide.subtask.buttonsExample')} />
        </div>
        <p>{t('guide.subtask.clients')}</p>
        <p className="text-content-subtle">{t('guide.subtask.safe')}</p>
      </GuideSection>

      <GuideSection icon={<Forward />} title={t('guide.forward.title')}>
        <p>{t('guide.forward.body')}</p>
        <p className="text-content-subtle">{t('guide.forward.untouched')}</p>
      </GuideSection>

      <GuideSection icon={<MessageSquareQuote />} title={t('guide.mentions.title')}>
        <p>{t('guide.mentions.body')}</p>
      </GuideSection>

      <GuideSection icon={<PartyPopper />} title={t('guide.praise.title')}>
        <p>{t('guide.praise.body')}</p>
      </GuideSection>

      <GuideSection icon={<CalendarClock />} title={t('guide.where.title')}>
        <p>{t('guide.where.body')}</p>
      </GuideSection>
    </Page>
  );
}

function GuideSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8 border-b border-border pb-8 last:border-0">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 text-accent [&_svg]:h-3.5 [&_svg]:w-3.5">
          {icon}
        </span>
        {title}
      </h2>
      <div className="max-w-prose space-y-3 text-sm leading-relaxed text-content">
        {children}
      </div>
    </section>
  );
}

/** One message as the user would type it, with what the bot makes of it. */
function Example({ message, means }: { message: string; means?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Bubble who="you">{message}</Bubble>
      {means ? (
        <span className="text-xs text-content-subtle">→ {means}</span>
      ) : null}
    </div>
  );
}

/** A short exchange: what you send, what the bot answers. */
function Conversation({ message, reply }: { message: string; reply: string }) {
  return (
    <div className="my-4 space-y-2">
      <div className="flex justify-start">
        <Bubble who="you">{message}</Bubble>
      </div>
      <div className="flex justify-start pl-6">
        <Bubble who="bot">{reply}</Bubble>
      </div>
    </div>
  );
}

function Bubble({ who, children }: { who: 'you' | 'bot'; children: React.ReactNode }) {
  return (
    <span
      className={
        who === 'you'
          ? 'inline-block whitespace-pre-line rounded-xl rounded-bl-sm bg-accent/10 px-3 py-1.5 text-sm text-content'
          : 'inline-block whitespace-pre-line rounded-xl rounded-bl-sm bg-surface px-3 py-1.5 text-sm text-content-muted ring-1 ring-border'
      }
    >
      {children}
    </span>
  );
}
